import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Last updated: April 27, 2026
      </p>

      <div className="space-y-8 text-sm leading-relaxed">
        <section>
          <h2 className="text-lg font-semibold mb-2">Overview</h2>
          <p>
            Lifter is a workout tracking app that uses AI to provide progressive
            overload recommendations. This policy describes what data we collect,
            how we use it, and your rights regarding that data. Lifter is
            operated by Matt McFadyen, a sole proprietor based in Canada.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Data We Collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Account information</strong>: name and email address
            </li>
            <li>
              <strong>Profile data</strong>: training goal, experience level,
              equipment preferences, focus areas, days per week, and injury or
              restriction notes
            </li>
            <li>
              <strong>Workout data</strong>: exercise sets (weight, reps, RPE),
              session timestamps, and AI-generated recommendations
            </li>
            <li>
              <strong>Session metadata</strong>: IP address and user agent
              (stored with authentication sessions)
            </li>
            <li>
              <strong>Analytics events</strong>: anonymized usage events (app
              opens, workout completions, subscription events) with no
              personally identifiable information
            </li>
            <li>
              <strong>Error reports</strong>: anonymized crash and error data
              with PII scrubbed before transmission
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">How We Use Your Data</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Authentication</strong>: your credentials are used to sign
              you in via email/password, Sign in with Apple, or Sign in with
              Google
            </li>
            <li>
              <strong>Workout tracking</strong>: your exercise data is stored so
              you can review history and track progress
            </li>
            <li>
              <strong>AI recommendations</strong>: your workout history, profile
              data, and injury notes are sent to an AI model to generate
              progressive overload targets. Injury notes are health-adjacent data
              — we do not market Lifter as a medical product and make no clinical
              claims
            </li>
            <li>
              <strong>Subscription management</strong>: subscription status is
              managed via RevenueCat and Apple in-app purchase
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Third-Party Processors</h2>
          <p className="mb-2">
            We do not sell your data. We do not use your data for advertising.
            Data is shared only with the processors below, limited to what is
            necessary for each service.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Anthropic</strong>: workout history and profile data are
              sent to Anthropic's Claude API to generate exercise recommendations.
              Anthropic does not use API data for model training per their API
              terms (verified April 2026).
            </li>
            <li>
              <strong>Turso</strong>: all application data is stored in a Turso
              (libSQL) database hosted on AWS us-east-1.
            </li>
            <li>
              <strong>Cloudflare</strong>: the API and web app are hosted on
              Cloudflare Workers and Pages.
            </li>
            <li>
              <strong>Apple</strong>: subscription billing and Sign in with Apple
              authentication. Apple's privacy policy applies to payment data.
            </li>
            <li>
              <strong>Google</strong>: Sign in with Google authentication, if
              used.
            </li>
            <li>
              <strong>RevenueCat</strong>: subscription entitlement management.
              RevenueCat receives your anonymized user ID and subscription state.
            </li>
            <li>
              <strong>Sentry</strong>: crash and error reporting. Events are
              scrubbed of PII before transmission.
            </li>
            <li>
              <strong>PostHog</strong>: product analytics and session replay for
              onboarding. Events use an anonymized user ID — no name or email is
              transmitted.
            </li>
            <li>
              <strong>Resend</strong>: transactional email delivery (password
              reset, account notifications).
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Data Retention</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Account and workout data: retained for the lifetime of your account</li>
            <li>Analytics events: 12 months (PostHog default)</li>
            <li>Error logs: 30 days</li>
            <li>
              On account deletion, all personal data is immediately and
              permanently removed from our systems
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Account Deletion</h2>
          <p>
            You can delete your account from the Account screen in the Lifter
            app. This action is permanent and cannot be undone. All associated
            data — workout history, exercise sets, profile information, and
            authentication records — is immediately removed.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Your Rights</h2>
          <p>
            You may request a copy of your data or ask us to delete it by
            contacting support. Canadian users have rights under PIPEDA. The
            primary mechanism for deletion is the in-app account deletion flow.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Contact</h2>
          <p>
            For questions about this privacy policy or your data, contact us at{" "}
            <a
              href="mailto:support@getlifter.app"
              className="text-primary underline"
            >
              support@getlifter.app
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
